import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { MachineMeasurementDto } from 'src/app/shared/dto/machine-measurements.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { MachineMeasurementsService } from '../../services/machine-measurements.service';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-machine-values',
    standalone: true,
    imports: [
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './machine-values.component.html',
    styleUrl: './machine-values.component.scss'
})
export class MachineValuesComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    machineMeasurements: MachineMeasurementDto[] = [];
    fieldsReadOnly: boolean = true;
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    machineMeasurementSelected: MachineMeasurementDto = new MachineMeasurementDto();
    createdAt: Date = new Date();
    updatedAt: Date = new Date();
    currentUser: UserDto = new UserDto();
    isLoading: boolean = true;
    isSaving: boolean = false;
    
    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        measurement: FormControl<string>;
    }>;

    private readonly machineMeasurementsService = inject(MachineMeasurementsService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('machineMeasurements', '');
        this.validateForm = this.fb.group({
            measurement: ['', [Validators.required]]
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.machineMeasurementsService.setupWebSocket();

        this.machineMeasurementsService.machineMeasurements$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (machineMeasurements: MachineMeasurementDto[]) => {
                    this.machineMeasurements = machineMeasurements;
                    this.machineMeasurementSelected = this.machineMeasurements.find(mm => mm.id === this.machineMeasurementSelected.id) || new MachineMeasurementDto();
                },
                error: () => {
                    this.message.error('Error cargando valores de máquina');
                }
            });

        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.machineMeasurementsService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    create() {
        this.machineMeasurementSelected = new MachineMeasurementDto();
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nuevo valor de máquina';
        this.fieldsReadOnly = false;
        this.validateForm.enable();
        this.openDrawer();
    }

    view(data: MachineMeasurementDto) {
        this.validateForm.patchValue(data);
        this.validateForm.disable();
        this.machineMeasurementSelected = data;
        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo valor de máquina ' + this.machineMeasurementSelected?.measurement;
        this.fieldsReadOnly = true;
        this.openDrawer();
    }

    edit(data: MachineMeasurementDto) {
        this.machineMeasurementSelected = data;
        this.validateForm.patchValue(data);
        this.validateForm.enable();
        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando valor de máquina ' + this.machineMeasurementSelected?.measurement;
        this.fieldsReadOnly = false;
        this.openDrawer();
    }

    setData() {
        // This does nothing, it's just for consistency
    }

    cancelEditing() {
        this.closeDrawer();
    }

    delete(id: number) {
        if (
            window.confirm(
                '¿Estás seguro de que quieres eliminar este valor de máquina? Esta acción no se puede deshacer.',
            )
        ) {
            this.machineMeasurementsService.delete(id).subscribe({
                next: (machineValue: MachineMeasurementDto) => {
                    try {
                        if (machineValue == undefined) {
                            throw new Error('Machine value not found');
                        }
                        const index = this.machineMeasurements.findIndex((p) => p.id === id);
                        if (index < 0) {
                            this.message.create('error', `No se ha podido eliminar el valor de máquina ${machineValue.measurement} con id ${id}, valor de máquina no encontrado`);
                            throw new Error('Machine value not found');
                        }
                        this.machineMeasurements.splice(index, 1);
                        const data = [...this.machineMeasurements];
                        this.machineMeasurements = data;
                        this.message.create('success', 'Valor de máquina eliminado correctamente',);
                    } catch (error) {
                        this.message.create('error', 'Error eliminando valor de máquina');
                        console.error(error);
                    }
                },
                error: (error) => {
                    this.message.create('error', 'Error eliminando valor de máquina, error de servidor');
                    console.error(error);
                },
            });
        } else {
            console.log(
                'Cancelando eliminación de valor de máquina ' + id,
            );
        }
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    closeDrawer() {
        this.machineMeasurementSelected = new MachineMeasurementDto();
        this.validateForm.reset();
        this.drawerVisible = false;
    }

    submitForm() {
        this.isSaving = true;

        const customMachineMeasurementDto: MachineMeasurementDto = {
            id: this.machineMeasurementSelected.id,
            measurement: this.validateForm.controls.measurement.value ? this.validateForm.controls.measurement.value : ''
        };

        if (!this.validForm(customMachineMeasurementDto)) {
            this.isSaving = false;
            return;
        }

        if (this.drawerMode === DrawerMode.CREATE) {
            this.machineMeasurementsService.save(customMachineMeasurementDto).subscribe({
                next: (machineMeasurement) => {
                    try {
                        if (machineMeasurement == undefined) {
                            this.isSaving = false;
                            throw new Error('Error creating machine value');
                        }
                        this.machineMeasurements.push(machineMeasurement);
                        const data = [...this.machineMeasurements];
                        this.machineMeasurements = data;

                        this.isSaving = false;
                        this.message.create('success', 'Valor de máquina creado correctamente');
                        this.closeDrawer();
                    } catch (error) {
                        this.isSaving = false;
                        this.message.create('error', 'Error creando valor de máquina');
                        console.error('Error creating route', error);
                    }
                },
                error: (error) => {
                    this.isSaving = false;
                    this.message.create('error', 'Error creando valor de máquina, error de servidor');
                    console.error('Error creating route', error);
                },
            });
        } else if (this.drawerMode === DrawerMode.EDIT) {
            this.machineMeasurementsService.update(customMachineMeasurementDto.id, customMachineMeasurementDto).subscribe({
                next: (machineMeasurement: MachineMeasurementDto) => {
                    try {
                        if (machineMeasurement == undefined) {
                            this.isSaving = false;
                            throw new Error('Error updating machine measurements');
                        }
                        const index = this.machineMeasurements.findIndex(
                            (r) => r.id === machineMeasurement.id,
                        );
                        if (index < 0) {
                            this.isSaving = false;
                            this.message.create('error', `Valor de máquina ${customMachineMeasurementDto.measurement} con id ${customMachineMeasurementDto.id} no encontrado`);
                            throw new Error('Machine value not found');
                        }
                        this.machineMeasurements[index] = machineMeasurement;
                        const data = [...this.machineMeasurements];
                        this.machineMeasurements = data;

                        this.isSaving = false;
                        this.message.create('success', 'Valor de máquina actualizado correctamente',);
                        this.closeDrawer();
                    } catch (error) {
                        this.isSaving = false;
                        this.message.create('error', 'Error actualizando valor de máquina');
                        console.error('Error updating machine measurements', error);
                    }
                },
                error: (error) => {
                    this.isSaving = false;
                    this.message.create('error', 'Error actualizando valor de máquina');
                    console.error('Error updating machine measurements', error);
                },
            });
        }
    }

    validForm(machineMeasurement: MachineMeasurementDto): boolean {
        if (!machineMeasurement.measurement) {
            this.message.create(
                'error',
                'La medida es obligatoria',
            );
            return false;
        }
        return true;
    }
}
